import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight } from 'lucide-react';
import Container from '../components/layout/Container';
import SectionTitle from '../components/ui/SectionTitle';
import Button from '../components/ui/Button';

const BlogPage: React.FC = () => {
  const featuredPost = {
    id: '1',
    title: '10 Ways to Optimize Your IDX Website for Lead Generation',
    excerpt: 'Learn how to maximize your real estate website\'s potential to capture and convert more leads with these proven strategies.',
    date: 'May 15, 2023',
    author: 'Sarah Johnson',
    category: 'Lead Generation',
    image: 'https://images.pexels.com/photos/3760069/pexels-photo-3760069.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    slug: 'optimize-idx-website-lead-generation',
  };

  const blogPosts = [
    {
      id: '2',
      title: 'The Future of AI in Real Estate: What Agents Need to Know',
      excerpt: 'Artificial intelligence is transforming the real estate industry. Discover the key trends and how they will impact your business.',
      date: 'April 28, 2023',
      author: 'Michael Rodriguez',
      category: 'Technology',
      image: 'https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      slug: 'ai-in-real-estate',
    },
    {
      id: '3',
      title: 'SEO Strategies Specifically for Real Estate Professionals',
      excerpt: 'Improve your online visibility with these specialized SEO tactics designed for real estate websites and content.',
      date: 'April 12, 2023',
      author: 'Jennifer Parker',
      category: 'Marketing',
      image: 'https://images.pexels.com/photos/106344/pexels-photo-106344.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      slug: 'seo-strategies-real-estate',
    },
    {
      id: '4',
      title: 'How to Create Compelling Virtual Tours That Convert',
      excerpt: 'Virtual tours have become essential in modern real estate. Learn how to create tours that engage buyers and drive conversions.',
      date: 'March 30, 2023',
      author: 'David Kim',
      category: 'Marketing',
      image: 'https://images.pexels.com/photos/1643383/pexels-photo-1643383.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      slug: 'virtual-tours-that-convert',
    },
    {
      id: '5',
      title: 'Leveraging Market Reports to Build Client Trust and Authority',
      excerpt: 'Market reports are more than just data\u2014they\'re powerful tools for establishing your expertise. Here\'s how to use them effectively.',
      date: 'March 15, 2023',
      author: 'Emily Watson',
      category: 'Client Relations',
      image: 'https://images.pexels.com/photos/7681098/pexels-photo-7681098.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      slug: 'market-reports-client-trust',
    },
    {
      id: '6',
      title: 'Social Media Strategies for Luxury Real Estate Marketing',
      excerpt: 'Discover specialized social media tactics that work specifically for marketing high-end properties and attracting affluent clients.',
      date: 'February 28, 2023',
      author: 'James Thompson',
      category: 'Luxury Market',
      image: 'https://images.pexels.com/photos/1732414/pexels-photo-1732414.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      slug: 'social-media-luxury-real-estate',
    },
  ];

  const categories = [
    'All Categories',
    'Lead Generation',
    'Technology',
    'Marketing',
    'Client Relations',
    'Luxury Market',
    'Industry Trends',
  ];

  return (
    <>
      <section className="pt-32 pb-16">
        <Container>
          <SectionTitle
            title="Real Estate Insights Blog"
            subtitle="Expert tips, industry trends, and strategies to grow your real estate business"
            centered
          />

          {/* Featured Post */}
          <motion.div
            className="bg-white rounded-lg overflow-hidden shadow-soft mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="h-64 md:h-auto">
                <img 
                  src={featuredPost.image} 
                  alt={featuredPost.title} 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 md:p-8 flex flex-col justify-between">
                <div>
                  <div className="mb-3">
                    <span className="inline-block bg-primary-100 text-primary-700 text-sm font-medium px-3 py-1 rounded-full">
                      {featuredPost.category}
                    </span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-serif mb-4">
                    {featuredPost.title}
                  </h2>
                  <p className="text-neutral-600 mb-6">
                    {featuredPost.excerpt}
                  </p>
                </div>
                <div>
                  <div className="flex items-center text-sm text-neutral-500 mb-4">
                    <div className="flex items-center mr-4">
                      <Calendar size={16} className="mr-1" />
                      <span>{featuredPost.date}</span>
                    </div>
                    <div className="flex items-center">
                      <User size={16} className="mr-1" />
                      <span>{featuredPost.author}</span>
                    </div>
                  </div>
                  <Button variant="primary" to={`/blog/${featuredPost.slug}`}>
                    Read Article
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Categories */}
          <div className="mb-10 overflow-x-auto">
            <div className="flex space-x-2 pb-2">
              {categories.map((category, index) => (
                <button
                  key={index}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                    index === 0
                      ? 'bg-primary-600 text-white'
                      : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Blog Posts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <motion.div
                key={post.id}
                className="bg-white rounded-lg overflow-hidden shadow-soft border border-neutral-200 flex flex-col h-full"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="h-48 overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title} 
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="p-6 flex-grow">
                  <div className="mb-3">
                    <span className="inline-block bg-primary-100 text-primary-700 text-xs font-medium px-2 py-1 rounded-full">
                      {post.category}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold mb-3">
                    {post.title}
                  </h3>
                  <p className="text-neutral-600 mb-4">
                    {post.excerpt}
                  </p>
                </div>
                <div className="px-6 pb-6">
                  <div className="flex items-center text-sm text-neutral-500 mb-4">
                    <div className="flex items-center mr-4">
                      <Calendar size={14} className="mr-1" />
                      <span>{post.date}</span>
                    </div>
                    <div className="flex items-center">
                      <User size={14} className="mr-1" />
                      <span>{post.author}</span>
                    </div>
                  </div>
                  <a 
                    href={`/blog/${post.slug}`}
                    className="inline-flex items-center text-primary-600 font-medium hover:text-primary-700"
                  >
                    Read More <ArrowRight size={16} className="ml-1" />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Pagination */}
          <div className="mt-12 flex justify-center">
            <div className="inline-flex bg-white rounded-md shadow-sm">
              <button className="px-4 py-2 text-sm font-medium text-neutral-500 border border-neutral-300 rounded-l-md hover:bg-neutral-50">
                Previous
              </button>
              <button className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-primary-600">
                1
              </button>
              <button className="px-4 py-2 text-sm font-medium text-neutral-500 border-t border-b border-neutral-300 hover:bg-neutral-50">
                2
              </button>
              <button className="px-4 py-2 text-sm font-medium text-neutral-500 border-t border-b border-neutral-300 hover:bg-neutral-50">
                3
              </button>
              <button className="px-4 py-2 text-sm font-medium text-neutral-500 border border-neutral-300 rounded-r-md hover:bg-neutral-50">
                Next
              </button>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
};

export default BlogPage;